import React, { useState, useEffect } from "react";
import { Badge, Popover, Button, Drawer, Tabs, Tag, Typography, Flex } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { showLoading, hideLoading } from "../redux/alertsSlice";
import { setUser } from "../redux/userSlice";
import axios from "axios";
import { toast } from "react-hot-toast";
import moment from "moment";
import {
  BellOutlined, CheckOutlined, DeleteOutlined,
  ClockCircleOutlined, CheckCircleOutlined,
  MedicineBoxOutlined, InfoCircleOutlined,
  ArrowRightOutlined, CloseOutlined
} from "@ant-design/icons";

const { Text } = Typography;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Style injection ────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("nb-styles")) return;
  const el = document.createElement("style");
  el.id = "nb-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

    .nb-bell {
      width: 40px; height: 40px; border-radius: 11px !important;
      border: none !important; padding: 0 !important;
      background: rgba(255,255,255,.13) !important;
      display: flex !important; align-items: center !important; justify-content: center !important;
      color: #2b2727ff !important; font-size: 17px !important;
      transition: background .2s, transform .2s !important; cursor: pointer;
    }
    .nb-bell:hover { background: rgba(255,255,255,.24) !important; transform: scale(1.06) !important; }

    .nb-popover .ant-popover-inner {
      border-radius: 16px !important; padding: 0 !important; overflow: hidden;
      box-shadow: 0 16px 48px rgba(0,0,0,.15) !important;
    }
    .nb-popover .ant-popover-inner-content { padding: 0 !important; }

    .nb-pop-header {
      padding: 13px 16px 11px;
      background: linear-gradient(135deg, ${SLATE} 0%, ${SLATE_MID} 100%);
      display: flex; align-items: center; justify-content: space-between;
    }
    .nb-pop-item {
      padding: 10px 15px; cursor: pointer;
      border-bottom: 1px solid #f1f5f9;
      display: flex; align-items: flex-start; gap: 10px;
      transition: background .15s;
    }
    .nb-pop-item:last-of-type { border-bottom: none; }
    .nb-pop-item:hover { background: #f0fdfb; }
    .nb-pop-icon {
      width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      display: flex; align-items: center; justify-content: center; font-size: 13px;
    }
    .nb-pop-footer { padding: 10px 14px; border-top: 1px solid #f1f5f9; background: ${SURFACE}; }
    .nb-show-all {
      width: 100% !important; border-radius: 10px !important; height: 35px !important;
      font-size: 12px !important; font-weight: 600 !important;
      background: linear-gradient(135deg, ${EMERALD}, ${TEAL}) !important;
      border: none !important; color: ${WHITE} !important;
      transition: transform .2s, box-shadow .2s !important;
    }
    .nb-show-all:hover { transform: translateY(-1px) !important; box-shadow: 0 6px 14px ${TEAL}44 !important; }

    .nb-drawer .ant-drawer-header {
      background: linear-gradient(135deg, ${SLATE} 0%, ${SLATE_MID} 100%) !important;
      border-bottom: none !important; padding: 18px 22px !important;
    }
    .nb-drawer .ant-drawer-title { color: ${WHITE} !important; font-family:'Sora',sans-serif !important; font-weight:700 !important; font-size:15px !important; }
    .nb-drawer .ant-drawer-close { color: ${MUTED} !important; }
    .nb-drawer .ant-drawer-body { padding: 0 !important; background: ${SURFACE} !important; }
    .nb-drawer .ant-tabs-nav { padding: 0 16px !important; background: ${WHITE} !important; margin-bottom:0 !important; box-shadow: 0 2px 8px rgba(0,0,0,.04); }
    .nb-drawer .ant-tabs-tab { font-family:'DM Sans',sans-serif !important; font-weight:600 !important; font-size:13px !important; }
    .nb-drawer .ant-tabs-tab-active .ant-tabs-tab-btn { color: ${EMERALD} !important; }
    .nb-drawer .ant-tabs-ink-bar { background: ${EMERALD} !important; }
    .nb-drawer .ant-tabs-content-holder { padding: 12px 12px 0 !important; }

    .nb-card {
      background: ${WHITE}; border-radius: 13px; border: 1.5px solid #e2e8f0;
      padding: 12px 13px; margin-bottom: 8px; cursor: pointer;
      position: relative; overflow: hidden;
      transition: border-color .2s, box-shadow .15s, transform .15s;
    }
    .nb-card:hover { border-color: ${TEAL}; box-shadow: 0 5px 18px rgba(13,148,136,.1); transform: translateY(-1px); }
    .nb-card.unseen::before { content:''; position:absolute; left:0; top:0; bottom:0; width:3px; background:linear-gradient(180deg,${EMERALD},${TEAL}); border-radius:3px 0 0 3px; }
    .nb-card.seen::before   { content:''; position:absolute; left:0; top:0; bottom:0; width:3px; background:#e2e8f0; border-radius:3px 0 0 3px; }
    .nb-card.seen { opacity:.82; }
    .nb-card-icon { width:33px; height:33px; border-radius:9px; flex-shrink:0; display:flex; align-items:center; justify-content:center; font-size:14px; }
    .nb-card-icon.unseen { background:${TEAL_LIGHT}; color:${EMERALD}; border:1px solid #99f6e4; }
    .nb-card-icon.seen   { background:${SURFACE};    color:${MUTED};   border:1px solid #e2e8f0; }

    .nb-action-bar { display:flex; justify-content:flex-end; padding:7px 2px 10px; }
    .nb-action-btn { border-radius:8px !important; height:32px !important; font-size:12px !important; font-weight:600 !important; border:1.5px solid #e2e8f0 !important; color:${SLATE_MID} !important; transition:border-color .2s,color .2s !important; }
    .nb-action-btn:hover { border-color:${TEAL} !important; color:${EMERALD} !important; }
    .nb-action-btn.del:hover { border-color:#ef4444 !important; color:#ef4444 !important; }

    .nb-scroll { max-height:calc(100vh - 210px); overflow-y:auto; padding-bottom:20px; }
    .nb-scroll::-webkit-scrollbar { width:4px; }
    .nb-scroll::-webkit-scrollbar-thumb { background:${TEAL_LIGHT}; border-radius:4px; }

    .nb-empty { padding:36px 16px; text-align:center; }

    /* ── Custom popup (replaces antd notification entirely) ── */
    .nb-popup {
      position: fixed; top: 20px; right: 20px; z-index: 2000;
      background: ${WHITE}; border-radius: 16px;
      box-shadow: 0 20px 60px rgba(0,0,0,.15), 0 0 0 1px #e2e8f0;
      width: 500px; overflow: hidden;
      animation: nbPopIn .4s cubic-bezier(.175,.885,.32,1.275) both;
    }
    .nb-popup-bar {
      height: 4px;
      background: linear-gradient(90deg, ${EMERALD}, ${TEAL});
      animation: nbBar 8s linear forwards;
    }
    .nb-popup-body { padding: 14px 16px 16px; }
    .nb-popup-head { display:flex; align-items:center; gap:10px; margin-bottom:10px; }
    .nb-popup-icon {
      width:36px; height:36px; border-radius:10px; flex-shrink:0;
      background:${TEAL_LIGHT}; color:${EMERALD};
      display:flex; align-items:center; justify-content:center; font-size:17px;
    }
    .nb-popup-close {
      margin-left:auto; width:28px; height:28px; border-radius:8px;
      border:none !important; background:${SURFACE} !important; cursor:pointer;
      display:flex; align-items:center; justify-content:center; color:${MUTED} !important;
      transition:background .15s !important;
    }
    .nb-popup-close:hover { background:#f1f5f9 !important; color:${SLATE_MID} !important; }
    .nb-popup-actions { display:flex; gap:8px; margin-top:12px; }
    .nb-popup-dismiss {
      flex:1; height:34px !important; border-radius:9px !important;
      font-size:12px !important; font-weight:600 !important;
      background:linear-gradient(135deg,${EMERALD},${TEAL}) !important;
      border:none !important; color:${WHITE} !important;
    }
    .nb-popup-never {
      height:34px !important; border-radius:9px !important; font-size:12px !important; font-weight:600 !important;
      border:1.5px solid #e2e8f0 !important; color:${SLATE_MID} !important;
    }
    .nb-popup-never:hover { border-color:#ef4444 !important; color:#ef4444 !important; }

    @keyframes nbPopIn { from{opacity:0;transform:translateX(24px) scale(.96)} to{opacity:1;transform:translateX(0) scale(1)} }
    @keyframes nbBar   { from{width:100%} to{width:0%} }
    @keyframes nbShake { 0%,100%{transform:rotate(0)} 15%{transform:rotate(15deg)} 30%{transform:rotate(-12deg)} 45%{transform:rotate(9deg)} 60%{transform:rotate(-6deg)} 75%{transform:rotate(4deg)} }
    .nb-shake { animation: nbShake .65s ease; }
    @keyframes nbSlide { from{opacity:0;transform:translateX(10px)} to{opacity:1;transform:translateX(0)} }
    .nb-card { animation: nbSlide .22s ease both; }
  `;
  document.head.appendChild(el);
};

/* ─── Sub-components ─────────────────────────────────────────── */
function NCard({ n, seen = false, onClick }) {
  return (
    <div className={`nb-card ${seen ? "seen" : "unseen"}`} onClick={onClick}>
      <Flex gap={10} align="flex-start">
        <div className={`nb-card-icon ${seen ? "seen" : "unseen"}`}>
          <MedicineBoxOutlined />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontSize: 13, color: seen ? SLATE_MID : SLATE, display: "block", lineHeight: 1.5, fontWeight: seen ? 400 : 500 }}>
            {n.message}
          </Text>
          {n.createdAt && (
            <Text style={{ fontSize: 11, color: MUTED, marginTop: 3, display: "block" }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              {moment(n.createdAt).fromNow()}
            </Text>
          )}
        </div>
        {!seen && <div style={{ width: 7, height: 7, borderRadius: "50%", background: TEAL, flexShrink: 0, marginTop: 5 }} />}
        {seen  && <CheckCircleOutlined style={{ color: "#d1fae5", fontSize: 13, flexShrink: 0, marginTop: 2 }} />}
      </Flex>
    </div>
  );
}

function EmptyState({ unseen }) {
  return (
    <div className="nb-empty">
      <div style={{
        width: 52, height: 52, borderRadius: "50%", margin: "0 auto 12px",
        background: unseen ? TEAL_LIGHT : SURFACE,
        border: unseen ? "none" : "1px solid #e2e8f0",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 22, color: unseen ? EMERALD : MUTED,
      }}>
        {unseen ? <CheckCircleOutlined /> : <InfoCircleOutlined />}
      </div>
      <Text strong style={{ display: "block", color: SLATE, marginBottom: 4, fontFamily: "'Sora', sans-serif" }}>
        {unseen ? "All caught up!" : "Nothing here yet"}
      </Text>
      <Text style={{ color: MUTED, fontSize: 12 }}>
        {unseen ? "No new notifications right now." : "Seen notifications will appear here."}
      </Text>
    </div>
  );
}

/* ─── Custom popup — no antd notification used here ─────────── */
function NotifPopup({ count, onView, onDismiss, onNever }) {
  return (
    <div className="nb-popup">
      <div className="nb-popup-bar" />
      <div className="nb-popup-body">
        <div className="nb-popup-head">
          <div className="nb-popup-icon"><BellOutlined /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text strong style={{ fontSize: 13, color: SLATE, display: "block", fontFamily: "'Sora', sans-serif" }}>
              Unread Notifications
            </Text>
            <Text style={{ fontSize: 11, color: MUTED }}>
              You have {count} unseen notification{count !== 1 ? "s" : ""}
            </Text>
          </div>
          <Button className="nb-popup-close" onClick={onDismiss} icon={<CloseOutlined style={{ fontSize: 11 }} />} />
        </div>
        <Text style={{ fontSize: 12, color: SLATE_MID, lineHeight: 1.5 }}>
          Head over to your notifications to stay up to date with your appointments and activity.
        </Text>
        <div className="nb-popup-actions">
          <Button className="nb-popup-dismiss" icon={<BellOutlined />} onClick={onView}>
            View Notifications
          </Button>
          <Button className="nb-popup-never" onClick={onNever}>
            Don't show again
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main export ────────────────────────────────────────────── */
const NotificationBell = () => {
  const { user }  = useSelector((s) => s.user);
  const dispatch  = useDispatch();
  const navigate  = useNavigate();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [ringing, setRinging]         = useState(false);
  const [showPopup, setShowPopup]     = useState(false);

  injectStyles();

  const localKey    = `notifDismissed_${user?._id}`;
  const unseenCount = user?.unseenNotifications?.length || 0;
  const quickItems  = (user?.unseenNotifications || []).slice().reverse().slice(0, 3);

  /* Auto-popup after 5 s — uses React state, NOT antd notification.open() */
  useEffect(() => {
    if (!unseenCount) return;
    if (localStorage.getItem(localKey) === "true") return;
    const t = setTimeout(() => setShowPopup(true), 5000);
    return () => clearTimeout(t);
  }, [unseenCount, localKey]);

  const dismissPopup  = () => setShowPopup(false);
  const neverPopup    = () => { localStorage.setItem(localKey, "true"); setShowPopup(false); };
  const viewFromPopup = () => { setShowPopup(false); setDrawerOpen(true); };

  const markAllSeen = async () => {
    try {
      dispatch(showLoading());
      const { data } = await axios.post("/api/user/mark-all-notifications-seen",
        { userId: user._id },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (data.success) { toast.success(data.message); dispatch(setUser(data.data)); }
      else toast.error(data.message);
    } catch { dispatch(hideLoading()); toast.error("Something went wrong"); }
  };

  const deleteAll = async () => {
    try {
      dispatch(showLoading());
      const { data } = await axios.post("/api/user/delete-all-notifications",
        { userId: user._id },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (data.success) { toast.success(data.message); dispatch(setUser(data.data)); }
      else toast.error(data.message);
    } catch { dispatch(hideLoading()); toast.error("Something went wrong"); }
  };

  const goTo = (n) => {
    setPopoverOpen(false); setDrawerOpen(false);
    if (n?.onClickPath) navigate(n.onClickPath);
    else if (user?.isAdmin) navigate("/admin/doctorslist");
    else if (user?.isDoctor) navigate("/doctor/reviews");
  };

  const handleBellClick = () => {
    setRinging(true);
    setTimeout(() => setRinging(false), 700);
    setShowPopup(false);
    if (unseenCount === 0) { setDrawerOpen(true); setPopoverOpen(false); }
  };

  /* Popover content */
  const popoverContent = (
    <div style={{ width: 290 }}>
      <div className="nb-pop-header">
        <Flex align="center" gap={8}>
          <div style={{ width: 26, height: 26, borderRadius: 7, background: `${TEAL}33`, display: "flex", alignItems: "center", justifyContent: "center", color: TEAL, fontSize: 13 }}>
            <BellOutlined />
          </div>
          <Text strong style={{ color: WHITE, fontFamily: "'Sora', sans-serif", fontSize: 13 }}>Recent</Text>
        </Flex>
        <Tag style={{ background: `${TEAL}33`, color: TEAL, border: "none", borderRadius: 20, fontSize: 10, fontWeight: 700 }}>
          {unseenCount} new
        </Tag>
      </div>

      {quickItems.length === 0 ? (
        <div style={{ padding: "18px 16px", textAlign: "center" }}>
          <Text style={{ color: MUTED, fontSize: 12 }}>No new notifications.</Text>
        </div>
      ) : (
        quickItems.map((n, i) => (
          <div key={n._id || i} className="nb-pop-item" onClick={() => goTo(n)}>
            <div className="nb-pop-icon"><MedicineBoxOutlined /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ fontSize: 12, color: SLATE, display: "block", lineHeight: 1.4, fontWeight: 500 }}>
                {n.message}
              </Text>
              {n.createdAt && (
                <Text style={{ fontSize: 10, color: MUTED, marginTop: 2, display: "block" }}>
                  {moment(n.createdAt).fromNow()}
                </Text>
              )}
            </div>
          </div>
        ))
      )}

      <div className="nb-pop-footer">
        <Button className="nb-show-all" icon={<ArrowRightOutlined />}
          onClick={() => { setDrawerOpen(true); setPopoverOpen(false); }}>
          {unseenCount > 3 ? `View all ${unseenCount} notifications` : "View all notifications"}
        </Button>
      </div>
    </div>
  );

  /* Drawer title */
  const drawerTitle = (
    <Flex align="center" gap={10}>
      <div style={{ width: 32, height: 32, borderRadius: 9, background: `${TEAL}33`, display: "flex", alignItems: "center", justifyContent: "center", color: TEAL, fontSize: 15 }}>
        <BellOutlined />
      </div>
      <div>
        <Text strong style={{ color: WHITE, fontFamily: "'Sora', sans-serif", fontSize: 14, display: "block", lineHeight: 1.2 }}>
          Notifications
        </Text>
        {unseenCount > 0 && (
          <Text style={{ color: TEAL, fontSize: 11, fontWeight: 600 }}>{unseenCount} unread</Text>
        )}
      </div>
    </Flex>
  );

  /* Tab items */
  const tabItems = [
    {
      key: "0",
      label: (
        <Flex align="center" gap={5}>
          <BellOutlined />
          Unseen
          {unseenCount > 0 && (
            <Tag style={{ background: TEAL_LIGHT, color: EMERALD, border: "none", borderRadius: 20, fontSize: 10, fontWeight: 700, padding: "0 6px", lineHeight: "18px" }}>
              {unseenCount}
            </Tag>
          )}
        </Flex>
      ),
      children: (
        <div>
          {unseenCount > 0 && (
            <div className="nb-action-bar">
              <Button className="nb-action-btn" icon={<CheckOutlined />} onClick={markAllSeen}>Mark all seen</Button>
            </div>
          )}
          <div className="nb-scroll">
            {unseenCount === 0 ? <EmptyState unseen /> : (
              (user?.unseenNotifications || []).slice().reverse().map((n, i) => (
                <NCard key={n._id || i} n={n} seen={false} onClick={() => goTo(n)} />
              ))
            )}
          </div>
        </div>
      ),
    },
    {
      key: "1",
      label: (
        <Flex align="center" gap={5}>
          <CheckCircleOutlined />
          Seen
          {(user?.seenNotifications?.length || 0) > 0 && (
            <Tag style={{ background: SURFACE, color: MUTED, border: "1px solid #e2e8f0", borderRadius: 20, fontSize: 10, fontWeight: 700, padding: "0 6px", lineHeight: "18px" }}>
              {user.seenNotifications.length}
            </Tag>
          )}
        </Flex>
      ),
      children: (
        <div>
          {(user?.seenNotifications?.length || 0) > 0 && (
            <div className="nb-action-bar">
              <Button className="nb-action-btn del" icon={<DeleteOutlined />} onClick={deleteAll}>Delete all</Button>
            </div>
          )}
          <div className="nb-scroll">
            {(user?.seenNotifications?.length || 0) === 0 ? <EmptyState unseen={false} /> : (
              user.seenNotifications.map((n, i) => (
                <NCard key={n._id || i} n={n} seen onClick={() => goTo(n)} />
              ))
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <Popover
        overlayClassName="nb-popover"
        content={popoverContent}
        trigger="click"
        placement="bottomRight"
        open={popoverOpen && unseenCount > 0}
        onOpenChange={(v) => unseenCount > 0 && setPopoverOpen(v)}
      >
        <Badge
          count={unseenCount}
          size="small"
          offset={[-3, 4]}
          style={{ background: "#ef4444", boxShadow: "0 0 0 2px rgba(15,23,42,.55)" }}
        >
          <Button className={`nb-bell ${ringing ? "nb-shake" : ""}`} onClick={handleBellClick}>
            <BellOutlined />
          </Button>
        </Badge>
      </Popover>

      <Drawer
        className="nb-drawer"
        title={drawerTitle}
        width={400}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Tabs defaultActiveKey="0" items={tabItems} />
      </Drawer>

      {showPopup && (
        <NotifPopup
          count={unseenCount}
          onView={viewFromPopup}
          onDismiss={dismissPopup}
          onNever={neverPopup}
        />
      )}
    </>
  );
};

export default NotificationBell;